import { dasherizeParts, FlagState, Stat } from '@timing71/common';

export function getManifest(client) {
  return {
    name: client.race?.series || 'FIA WEC',
    description: dasherizeParts(client.race?.name, client.data?.name),
    colSpec: [
      Stat.NUM,
      Stat.STATE,
      Stat.CLASS,
      Stat.POS_IN_CLASS,
      Stat.TEAM,
      Stat.DRIVER,
      Stat.CAR,
      Stat.LAPS,
      Stat.GAP, // 8
      Stat.INT, // 9
      Stat.S1,
      Stat.BS1,
      Stat.S2,
      Stat.BS2,
      Stat.S3,
      Stat.BS3,
      Stat.LAST_LAP,
      Stat.BEST_LAP,
      Stat.PITS
    ],
    trackDataSpec: [
      'Track temp',
      'Air temp',
      'Humidity',
      'Pressure',
      'Wind speed',
      'Wind direction'
    ]
  }
}

export function getState(client) {
  return {
    cars: postprocessCars(mapCars(client), client.bestSectors),
    session: mapSession(client)
  }
}

const FLAGS = {
  'Green': FlagState.GREEN,
  'Red': FlagState.RED,
  'Yellow': FlagState.YELLOW,
  'FullYellow': FlagState.FCY,
  'SafetyCar': FlagState.SC,
  'VirtualSafetyCar': FlagState.VSC,
  'Finish': FlagState.CHEQUERED
}

function mapSession(client) {
  const status = client.data?.liveStatus || {};

  const now = Date.now();
  const startTime = new Date(status.sessionStartTime || 0);
  const delta = now - new Date(client.data?.date);

  const elapsed = status.sessionStartTime ? Math.max(0, now - startTime + delta) : 0;

  const elapsedRunning = elapsed - (status.stoppedSeconds * 1000);

  const remaining = status?.finalDurationSeconds ? Math.max(0, status.finalDurationSeconds - Math.floor(elapsedRunning / 1000)) : client.data.duration;

  return {
    flagState: FLAGS[status.currentFlag?.type] || FlagState.NONE,
    timeElapsed: Math.ceil(elapsed / 1000),
    timeRemain: remaining,
    pauseClocks: !status.sessionStartTime,
    trackData: mapTrackData(client)
  }
}

function mapTrackData(client) {
  return [
    [client.data.weather?.trackTemperatureEx?.celsiusDegrees ? Math.round((client.data.weather.trackTemperatureEx.celsiusDegrees) * 10) / 10 : null, '°C'],
    [client.data.weather?.ambientTemperatureEx?.celsiusDegrees ? Math.round((client.data.weather.ambientTemperatureEx.celsiusDegrees) * 10) / 10 : null, '°C'],
    [client.data.weather?.humidityPercent ? Math.round(client.data.weather.humidityPercent) : null, '%'],
    [client.data.weather?.pressure?.mBar ? Math.round((client.data.weather.pressure.mBar) * 10) / 10 : null, ' mbar'],
    [client.data.weather?.windSpeed?.milesPerHour ? Math.round((client.data.weather.windSpeed.milesPerHour) * 10) / 10 : null, ' mph'],
    [client.data.weather?.windDirectionDegrees ? Math.round(client.data.weather.windDirectionDegrees) : null, '°']
  ]
}

const STATES = {
  'InBox': 'PIT',
  'OnTrack': 'RUN',
  'FirstLapAfterPit': 'OUT',
  'StoppedOnTrack': 'STOP'
};

const TIME_FLAGS = {
  'PersonalBest': 'pb',
  'OverallBest': 'sb',
  'Invalid': 'invalid',
  'Neutral': ''
}

function gapBetween(leader, follower) {
  const leaderLap = (leader?.lapNumber || 0);
  const followerLap = (follower?.lapNumber || 0)
  if (leaderLap !== followerLap) {
    const diff = followerLap - leaderLap;
    if (diff === 0) {
      return ''
    }
    return `${diff} lap${diff === 1 ? '' : 's'}`;
  }

  const diffSecs = ((follower?.timeMilliseconds || 0) - (leader?.timeMilliseconds || 0)) / 1000;

  if (diffSecs >= 0) {
    // Can sometimes return negative values around pit stops
    return diffSecs;
  }

  return null;
}

function formatClassName(className) {
  if (!className) {
    return null;
  }
  return className.replaceAll('LM ', 'LM').replaceAll('Hypercar H', 'Hypercar')
}

function mapCars(client) {

  const categories = Object.fromEntries(client.data.categories.map(c => [c.id, c]));
  return client.data.participants.map(
    (c, idx) => {

      const prevCar = client.data.participants[idx - 1];

      const gap = gapBetween(null, c.firstParticipantGap);
      const prevGap = gapBetween(prevCar?.firstParticipantGap, c.firstParticipantGap);

      const sectorCols = [1, 2, 3].flatMap(
        (sectorNum) => {

          const lastSector = (c.lastCompletedSectors || [])[sectorNum - 1];

          const sectorFlag = c.lastCompletedSectors?.length > sectorNum ? 'old' : '';

          return [
            [lastSector?.lapTime ? lastSector.lapTime / 1000 : '', TIME_FLAGS[lastSector?.state] || sectorFlag],
            ['', '']
          ]
        }
      );

      const refData = client.refData?.entries?.find(e => e.number === c.number);

      const lastLap = (c.lastLap?.timeMilliseconds || 0) / 1000;
      const bestLap = (c.bestLap?.timeMilliseconds || 0) / 1000;

      const llFlag = TIME_FLAGS[c.lastLap?.state] || (lastLap == bestLap ? 'pb' : '');

      const driverRefData = client.refData?.drivers?.find(d => d.id == c.driver?.id);
      const driverRank = driverRefData?.rank?.toLowerCase() || 'unknown';

      return [
        c.number,
        c.hasSeenCheckeredFlag ? 'FIN' : STATES[c.status] || c.status,
        formatClassName(refData?.category_label || categories[c.category.id].label),
        c.positionInCategory,
        c.team.name,
        c.driver ? [`${c.driver.lastName.toUpperCase()}, ${c.driver.firstName}`, `driver-${driverRank}`] : ['', ''],
        c.car?.name,
        c.completeLapsCount,
        gap,
        prevGap
      ].concat(sectorCols).concat([
        [lastLap > 0 ? lastLap : '', llFlag],
        [bestLap > 0 ? bestLap : '', 'old'],
        c.pitStopCount
      ])
    }
  )
}

const CLASS_IDX = 2;
const SECTOR_1_IDX = 10;
const S3_IDX = 14;
const LAST_LAP_IDX = 16;
const BEST_LAP_IDX = 17;

function postprocessCars(cars, bestSectors) {

  const bestLapsByClass = {};
  const bestSectorsByClass = {};

  cars.forEach(
    (car, idx) => {
      const num = car[0];
      const clazz = car[CLASS_IDX];
      const bestLap = car[BEST_LAP_IDX];

      if (!bestSectors[num]) {
        bestSectors[num] = [];
      }

      if (!bestSectorsByClass[clazz]) {
        bestSectorsByClass[clazz] = [];
      }

      const myBestSectors = bestSectors[num];
      const classBestSectors = bestSectorsByClass[clazz];

      [0, 1, 2].forEach(
        (sectorIdx) => {
          const sector = car[SECTOR_1_IDX + (2 * sectorIdx)];
          if (sector[1] === 'pb' || sector[1] === 'sb') {
            myBestSectors[sectorIdx] = sector[0];
          }

          const bestClassSector = classBestSectors[sectorIdx];

          if (myBestSectors[sectorIdx] > 0 && (!bestClassSector || bestClassSector.time > myBestSectors[sectorIdx])) {
            classBestSectors[sectorIdx] = { time: myBestSectors[sectorIdx], idx };
          }

          car[SECTOR_1_IDX + (2 * sectorIdx) + 1] = [myBestSectors[sectorIdx], 'old'];
        }
      );


      if (bestLap[0] > 0 && (!bestLapsByClass[clazz] || bestLapsByClass[clazz].time > bestLap[0])) {
        bestLapsByClass[clazz] = {
          time: bestLap[0],
          idx
        }
      }
    }
  )

  Object.values(bestLapsByClass).forEach(
    ({ _, idx }) => {
      const car = cars[idx];

      if (car[LAST_LAP_IDX][0] === car[BEST_LAP_IDX][0]) {
        car[LAST_LAP_IDX][1] = car[S3_IDX][0] > 0 ? 'sb-new' : 'sb';
      }

      car[BEST_LAP_IDX][1] = 'sb';
    }
  );

  Object.values(bestSectorsByClass).forEach(
    (sectors) => sectors.forEach(
      (sector, sectorIdx) => {
        if (sector) {
          const { time, idx } = sector;
          cars[idx][SECTOR_1_IDX + (2 * sectorIdx) + 1][1] = 'sb';

          if (cars[idx][SECTOR_1_IDX + (2 * sectorIdx)][0] === time) {
            cars[idx][SECTOR_1_IDX + (2 * sectorIdx)][1] = 'sb'
          }
        }
      }
    )
  );

  return cars;
}
